/**
 * "שיתוף בוואטסאפ" for a signing link: a `wa.me` link that opens the REALTOR'S OWN WhatsApp on
 * a chat with the client, with a short message and the signing link(s) already typed in.
 *
 * Why it exists (Trello #231, item 7): the automatic send ("שליחה אוטומטית ב-WhatsApp וב-SMS",
 * `resendWhatsapp`) goes out from Webot's system number, so it never shows up in the realtor's
 * own WhatsApp and realtors concluded nothing was sent — 20 of 46 realtors re-sent a link in the
 * 60 days to 2026-10-19, a third of those re-sends within ten minutes of the first. Competitors
 * offer the same pair: a system send, and a share that just opens the realtor's chat.
 *
 * This module only BUILDS a URL. Nothing here talks to Webot: opening the link sends no message,
 * writes nothing and changes no status or delivery counter. The document reads "נשלח" once the
 * client opens the link, exactly as after copying it.
 */
import type { TFunction } from "react-i18next";
import type { ListingListedFor, SignatureDocumentKind } from "../@types/sharedSchema";
import { toInternationalFormat } from "./Haim/phoneNumbersUtils";
import { isolateLtr } from "./bidi";
import { formatPhoneLocal } from "../services/signatureFormatters";
import { questionnaireLabelContext } from "./signatureQuestionnaireName";
import { carriesQuestionnaireStep, signerQuestionnaireStep } from "./signatureQuestionnaireStep";

/** The parts of a signer this needs — satisfied by the dashboard's `SignatureDocumentSigner`. */
export interface ShareableSigner {
  id: string;
  name?: string;
  phone?: string;
  signing?: { publicSigningUrl?: string };
  /** Where this signer is in the contract (its questionnaire step is only mentioned while still to fill). */
  mainSignedAt?: string;
  addendumSignedAt?: string;
  questionnaireSignedAt?: string;
}

/** The parts of a document this needs — satisfied by the dashboard's `SignatureDocument`. */
export interface ShareableDocument {
  id: string;
  templateNameSnapshot?: string;
  templateCustomerFacingNameSnapshot?: string;
  /** 'ownerQuestionnaire': a questionnaire on its own, filled in as well as signed. */
  documentKind?: SignatureDocumentKind;
  /** The contract carries the owner questionnaire as its last step ('rent' names it שאלון בעל זכות בנכס). */
  sellerQuestionnaire?: { listedFor?: ListingListedFor } | null;
  /** A hidden-addresses send: the addresses are confirmed between the contract and the questionnaire. */
  hideAddresses?: boolean;
  signers: ShareableSigner[];
}

export interface SigningLinkWhatsappShare {
  /** `https://wa.me/<digits>?text=…`, or `https://wa.me/?text=…` when the phone is unusable. */
  href: string;
  /** International digits of the chat that opens, or null: WhatsApp then asks whom to send to. */
  recipientDigits: string | null;
  /** The message as typed into the chat. */
  text: string;
  /** How many signing links the message carries: one, also for a set (Trello #231 item 6). */
  linkCount: number;
  /** How many documents that one link opens for the client (a set's documents; 1 otherwise). */
  documentCount: number;
}

/**
 * The international number as `wa.me` wants it: digits only, country code first, no "+".
 * Stored signer phones are E.164 (`+972501234567`) — but 1.7% of the last 60 days' signers were
 * stored in the local form (`0501234567`) and 6% abroad (+1, +33, +44, +49, +61…), so this goes
 * through the app's one phone parser rather than a regex. Null when nothing dialable is left.
 */
export function whatsappRecipientDigits(phone?: string | null): string | null {
  const raw = (phone ?? "").trim();
  if (!raw) return null;
  const digits = (toInternationalFormat(raw) ?? "").replace(/\D/g, "");
  // E.164 allows at most 15 digits; anything under 8 is not a phone number anywhere.
  return digits.length >= 8 && digits.length <= 15 ? digits : null;
}

/**
 * Who an AUTOMATIC send went to, for the confirmation ("הקישור נשלח ל-050-123-4567 …"): the
 * phone as the realtor reads it — local for Israel, international abroad; the name when there is
 * no phone. "050-123-4567" is digits and hyphens and already reads left-to-right inside Hebrew, so
 * it is left bare (the Playwright WebKit build draws the isolate characters as boxes); only a
 * "+33…" is isolated, or RTL text moves its "+" to the wrong end.
 */
export function deliveryRecipientLabel(signer?: { name?: string; phone?: string } | null): string {
  const phone = formatPhoneLocal(signer?.phone);
  if (!phone) return signer?.name?.trim() || "";
  // A word joiner after each hyphen keeps the number on one line: a confirmation wrapping inside it
  // put "050-" at the end of an RTL line ("ל--050") and the rest on the next. Not U+2011 (the
  // non-breaking hyphen): that is a bidi neutral, so in Hebrew text the number came out reversed
  // ("4567-123-050"). The joiner is ignored by the bidi algorithm, so "-" stays a number separator.
  return phone.startsWith("+") ? isolateLtr(phone) : phone.replace(/-/g, "-\u2060");
}

const firstNameOf = (name?: string): string => (name ?? "").trim().split(/\s+/)[0] ?? "";

/** What the CLIENT calls the document — the same rule as the system SMS (`sendSigningLinkSms`). */
const customerTitleOf = (doc: ShareableDocument): string =>
  doc.templateCustomerFacingNameSnapshot?.trim() || doc.templateNameSnapshot?.trim() || "";

/**
 * The signer of `doc` the share is FOR. The first document picks the person (`signerId`, else its
 * first signer); every other document of a set contributes the signer with the same phone, or
 * nothing. Never another person's link: a slug is that signer's own way in.
 */
function signerFor(doc: ShareableDocument, target: ShareableSigner): ShareableSigner | undefined {
  const byId = doc.signers.find((s) => s.id === target.id);
  if (byId) return byId;
  const targetDigits = whatsappRecipientDigits(target.phone);
  if (!targetDigits) return undefined;
  return doc.signers.find((s) => whatsappRecipientDigits(s.phone) === targetDigits);
}

/** i18next options that never HTML-escape a value: the text leaves the app inside a URL. */
type RawInterpolation = { interpolation: { escapeValue: false } };

/** One link the message carries, as the client will meet it. */
export type ShareLink = {
  title: string;
  url: string;
  /** A questionnaire on its own: filled in as well as signed. */
  questionnaire?: boolean;
  /** The contract brings the owner questionnaire right after its signature, still to fill ('rent' names it). */
  questionnaireStep?: { listedFor?: ListingListedFor };
};

/** The message typed into the chat, in the realtor's own voice (they send it, not Webot). */
export function signingLinkShareText(t: TFunction, name: string | undefined, links: ShareLink[]): string {
  // The text leaves the app inside a URL: never HTML-escape a value ("/" would become "&#x2F;"),
  // whatever the i18n instance was configured with.
  const raw: RawInterpolation = { interpolation: { escapeValue: false } };
  const firstName = firstNameOf(name);
  const greeting = firstName
    ? t("signatures.whatsappShare.greetingNamed", { name: firstName, ...raw })
    : t("signatures.whatsappShare.greeting");
  return withQuestionnaireNote(t, links, messageOf(t, greeting, links, raw), raw);
}

function messageOf(t: TFunction, greeting: string, links: ShareLink[], raw: RawInterpolation): string {
  if (links.length === 1) {
    const [{ title, url, questionnaire }] = links;
    // A questionnaire on its own is filled in, not only signed (its title, e.g. "שאלון מוכר", is set at send).
    if (title && questionnaire) return t("signatures.whatsappShare.messageOneQuestionnaire", { greeting, title, url, ...raw });
    return title
      ? t("signatures.whatsappShare.messageOne", { greeting, title, url, ...raw })
      : t("signatures.whatsappShare.messageOneUntitled", { greeting, url, ...raw });
  }
  // A set is ONE link, the first document's: after the SMS code the sign page lists every document
  // still waiting, so the message names them all and carries that one link (Haim, 2026-10-21).
  const titles = links.map(({ title }) => title).filter(Boolean).join(", ");
  return t("signatures.whatsappShare.messageSet", { greeting, total: links.length, titles, url: links[0].url, ...raw });
}

/**
 * A contract that brings the owner questionnaire tells the client it follows the signature (Trello
 * #231, item 8 follow-up): one line after the link, naming the contract when the message covers a set.
 * Nothing is added for any other message, so those read exactly as before.
 */
function withQuestionnaireNote(t: TFunction, links: ShareLink[], message: string, raw: RawInterpolation): string {
  const carrier = links.find((link) => link.questionnaireStep);
  if (!carrier) return message;
  const context = questionnaireLabelContext(carrier.questionnaireStep?.listedFor);
  const note =
    links.length > 1 && carrier.title
      ? t("signatures.whatsappShare.questionnaireAfterSet", { title: carrier.title, context, ...raw })
      : t("signatures.whatsappShare.questionnaireAfter", { context, ...raw });
  return `${message}\n${note}`;
}

/**
 * The share for one client: ONE signing link — the first document's — naming every document of the
 * set it opens for that client, in order. Null when there is no link to share (nothing prepared yet).
 * `signerId` picks the client on the FIRST document; default is its first signer, who is the one the
 * prepared dialog and the list act on.
 */
export function buildSigningLinkWhatsappShare(
  documents: ShareableDocument[],
  t: TFunction,
  signerId?: string
): SigningLinkWhatsappShare | null {
  const first = documents[0];
  const target = first && ((signerId && first.signers.find((s) => s.id === signerId)) || first.signers[0]);
  if (!target) return null;
  const links = documents.flatMap((doc): ShareLink[] => {
    const signer = signerFor(doc, target);
    const url = signer?.signing?.publicSigningUrl;
    if (!signer || !url) return [];
    // The questionnaire this contract brings, while this client still has it to fill.
    const questionnaireStep =
      carriesQuestionnaireStep(doc) && signerQuestionnaireStep(signer, !!doc.hideAddresses) !== "done"
        ? { listedFor: doc.sellerQuestionnaire?.listedFor }
        : undefined;
    return [{ title: customerTitleOf(doc), url, questionnaire: doc.documentKind === "ownerQuestionnaire", ...(questionnaireStep ? { questionnaireStep } : {}) }];
  });
  if (!links.length) return null;
  const recipientDigits = whatsappRecipientDigits(target.phone);
  const text = signingLinkShareText(t, target.name, links);
  const query = `?text=${encodeURIComponent(text)}`;
  return {
    href: recipientDigits ? `https://wa.me/${recipientDigits}${query}` : `https://wa.me/${query}`,
    recipientDigits,
    text,
    linkCount: 1,
    documentCount: links.length
  };
}
